import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, Trash, MoreVertical } from "lucide-react";

const StaffBlogList = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [deleteModal, setDeleteModal] = useState(false);
    const [blogToDelete, setBlogToDelete] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const toast = useRef(null);

    const breadcrumbItems = [
        { title: "Content", link: "/" },
        { title: "Blogs", link: "#!" },
    ];

    const statusOptions = [
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'archived', label: 'Archived' },
        { value: 'pending_review', label: 'Pending Review' }
    ];

    const fetchBlogs = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/blog/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            const blogData = response.data.blogs || [];
            setBlogs(blogData);
            setTotalRecords(blogData.length);
        } catch (error) {
            console.error("Error fetching blogs:", error);
            showToast('error', 'Error', 'Failed to fetch blogs');
        }
        setLoading(false);
    }, []);

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const toggleDropdown = (id) => setDropdownOpen(dropdownOpen === id ? null : id);

    const confirmDelete = (blog) => {
        setBlogToDelete(blog);
        setDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!blogToDelete) return;
        setLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/blog/delete/${blogToDelete.id}`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            showToast('success', 'Deleted', 'Blog deleted successfully');
            fetchBlogs();
        } catch (error) {
            console.error("Delete error:", error.response?.data || error.message);
            showToast('error', 'Error', 'Failed to delete blog');
        }
        setBlogToDelete(null);
        setDeleteModal(false);
        setLoading(false);
    };

    const handleEdit = (blogId) => {
        if (!blogId) {
            showToast('error', 'Error', 'Blog ID is missing');
            return;
        }
        navigate(`/staff/office/website/addblog?edit=${blogId}`);
    };

    const statusBodyTemplate = (rowData) => {
        const getStatusClass = (status) => {
            switch (status) {
                case 'draft': return 'secondary';
                case 'published': return 'success';
                case 'archived': return 'dark';
                case 'pending_review': return 'warning';
                default: return 'light';
            }
        };

        const statusLabel = statusOptions.find(opt => opt.value === rowData.status)?.label || rowData.status;
        const statusClass = getStatusClass(rowData.status);

        return (
            <span className={`badge bg-${statusClass}-subtle text-${statusClass} text-uppercase`}>
                {statusLabel}
            </span>
        );
    };

    const imageBodyTemplate = (rowData) => {
        return (
            <img
                src={rowData.image_url}
                alt={rowData.title}
                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                className="rounded"
                onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/50';
                    e.target.alt = 'Placeholder image';
                }}
            />
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <Dropdown
                isOpen={dropdownOpen === rowData.id}
                toggle={() => toggleDropdown(rowData.id)}
            >
                <DropdownToggle tag="button" className="btn btn-light">
                    <MoreVertical size={20} />
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem onClick={() => handleEdit(rowData.id)}>
                        <Edit size={16} className="me-2" />
                        Edit
                    </DropdownItem>

                    <DropdownItem
                        onClick={() => confirmDelete(rowData)}
                        className="text-danger"
                    >
                        <Trash size={16} className="me-2" />
                        Delete
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        );
    };

    const dateBodyTemplate = (rowData) => {
        return new Date(rowData.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Blogs" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0 flex-grow-1">Blog List</h4>
                            <Button
                                color="primary"
                                className="d-flex align-items-center gap-1"
                                onClick={() => navigate('/staff/office/website/addblog')}
                            >
                                <i className="mdi mdi-plus"></i> Add Blog
                            </Button>
                        </CardHeader>
                        <CardBody>
                            <DataTable
                                value={blogs.slice(first, first + rows)}
                                loading={loading}
                                stripedRows
                                className="border"
                                emptyMessage="No blogs found"
                            >
                                <Column header="SI.No" body={(_, { rowIndex }) => rowIndex + 1 + first} />
                                <Column header="Image" body={imageBodyTemplate} />
                                <Column header="Title" field="title" sortable />
                                <Column header="Status" body={statusBodyTemplate} sortable />
                                <Column header="Created At" body={dateBodyTemplate} sortable field="created_at" />
                                <Column header="Actions" body={actionBodyTemplate} style={{ width: '120px' }} />
                            </DataTable>
                            {totalRecords > 0 && (
                                <Paginator
                                    first={first}
                                    rows={rows}
                                    totalRecords={totalRecords}
                                    onPageChange={onPageChange}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    className="mt-3"
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
                <ModalHeader toggle={() => setDeleteModal(false)}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete the blog "{blogToDelete?.title}"?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setDeleteModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : 'Delete'}
                    </Button>
                </ModalFooter>
            </Modal>
            <Toast ref={toast} position="top-right" />
        </React.Fragment>
    );
};

export default StaffBlogList;